'use client';

import type GroupedTag from '@alltrails/types/GroupedTag';
import type Tag from '@alltrails/types/Tag';
import type TrailDetails from '@alltrails/core/types/TrailDetails';
import { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import dynamic from 'next/dynamic';
import useUser from '@alltrails/context/hooks/useUser';
import TrailDetailsOverviewLink from '@alltrails/amplitude/enums/TrailDetailsOverviewLink';
import logTrailDetailsOverviewLinkClicked from '@alltrails/amplitude/events/logTrailDetailsOverviewLinkClicked';
import logTrailDetailsPlanYourVisitShowMoreClicked from '@alltrails/amplitude/events/logTrailDetailsPlanYourVisitShowMoreClicked';
import logTrailDetailsPlanYourVisitViewed from '@alltrails/amplitude/events/logTrailDetailsPlanYourVisitViewed';
import Button from '@alltrails/denali/components/Button';
import TrailWheelchair from '@alltrails/denali/icons/TrailWheelchair';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import cardStyles from '../../components/CardModule/CardModule.module.scss';
import { HelpfulLinkAdminPanelSectionName } from '../Overview/HelpfulLink';
import GroupTag from './GroupTag';
import HelpfulLinkTag from './HelpfulLinkTag';
import styles from './PlanYourVisit.module.scss';

const PlanYourVisitModal = dynamic(() => import('./PlanYourVisitModal'));
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

// priority order of tags -> Fees, Trail Info, Obstacles, Features, Activities
const fees = 4;
const trailInfo = 3;
const obstacles = 5;
const features = 2;
const activities = 1;
const priorityOrder: number[] = [fees, trailInfo, obstacles, features, activities];

type PlanYourVisitProps = {
  isBot?: boolean;
  groupedTags: GroupedTag[];
  trailDetails: TrailDetails;
  trailId: number;
  isAdminOrPLPUser?: boolean;
};

const PlanYourVisit = ({ isBot = false, groupedTags, trailDetails, trailId, isAdminOrPLPUser }: PlanYourVisitProps) => {
  const intl = useIntl();
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useIsVisible(ref, { threshold: 1 });
  const planYourVisitRef = useRef<boolean>(false);
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const [isShowMoreModalOpen, setIsShowMoreModalOpen] = useState(false);
  const [initialModal, setInitialModal] = useState<string>();
  const isMobile = useIsMobileSizedScreen();
  const user = useUser();
  const showAccessibilityLink = !isMobile && !!trailDetails.accessibility;

  useEffect(() => {
    if (isVisible && !planYourVisitRef.current) {
      planYourVisitRef.current = true;
      logTrailDetailsPlanYourVisitViewed({ trail_id: String(trailId) });
    }
  }, [isVisible, trailId]);

  const tags = groupedTags.flatMap(group =>
    group.tags.map((tag: Tag) => ({
      name: tag.name,
      uid: tag.uid
    }))
  );

  if (isBot)
    return (
      <div className={styles.tagsContainer}>
        {tags &&
          tags.map(tag => (
            <div key={tag.uid} data-testid={tag.uid} className={styles.tagName}>
              {tag.name}
            </div>
          ))}
      </div>
    );

  const onShowMoreClick = () => {
    setInitialModal(undefined);
    logTrailDetailsPlanYourVisitShowMoreClicked({ trail_id: String(trailId) });
    if (!user) {
      setIsSignUpModalOpen(true);
      return;
    }

    setIsShowMoreModalOpen(true);
  };

  const onHelpfulLinkClick = (id: string, trailDetailsOverviewLink?: TrailDetailsOverviewLink) => {
    setInitialModal(id);
    if (trailDetailsOverviewLink) {
      logTrailDetailsOverviewLinkClicked({ overview_link_clicked: trailDetailsOverviewLink, trail_id: trailDetails.id });
    }
    if (!user) {
      setIsSignUpModalOpen(true);
      return;
    }

    setIsShowMoreModalOpen(true);
  };

  // On the card module we want to show max 6 tags
  const getSortedTags = (tagGroups: GroupedTag[]): Tag[] => {
    const tagLimits = new Map<number, number>([
      [fees, 2], // Fees: up to 2
      [trailInfo, 4], // Trail Info: up to 4
      [obstacles, 2], // Obstacles: up to 2
      [features, 2], // Features: up to 2
      [activities, 0] // Activities: remaining space
    ]);

    const sortedGroups = tagGroups.filter(group => group.tags.length > 0).sort((a, b) => priorityOrder.indexOf(a.id) - priorityOrder.indexOf(b.id));
    const result: Tag[] = [];
    let remainingSpace = isMobile ? 3 : 6;

    for (const group of sortedGroups) {
      const limit = tagLimits.get(group.id) ?? 0;
      let tags = [...group.tags];
      const tagsToAdd = limit === 0 ? tags.slice(0, remainingSpace) : tags.slice(0, Math.min(limit, remainingSpace));
      result.push(...tagsToAdd);
      remainingSpace -= tagsToAdd.length;
      if (remainingSpace <= 0) {
        if (showAccessibilityLink) {
          result.pop();
        }
        break;
      }
    }

    return result;
  };

  const sortedTags = groupedTags && groupedTags.length > 0 ? getSortedTags(groupedTags) : null;

  const getSortedGroups = (tagsData: GroupedTag[]): GroupedTag[] => {
    return tagsData.sort((a, b) => {
      const indexA = priorityOrder.indexOf(a.id);
      const indexB = priorityOrder.indexOf(b.id);
      return indexA - indexB;
    });
  };

  const sortedGroups = getSortedGroups(groupedTags);
  return (
    <div ref={ref} className={classNames(cardStyles.card, cardStyles.noBackgroundOnMobile)}>
      <div className={classNames(cardStyles.inner, cardStyles.adjustedPadding)}>
        <h2 className={classNames(cardStyles.cardTitle, cardStyles.planYourVisitMobileTitle)}>
          <FormattedMessage defaultMessage="Plan your visit" />
        </h2>
        {sortedTags && (
          <div className={styles.tagsContainer}>
            {sortedTags?.map(tag => (
              <GroupTag key={tag.uid} tag={tag} isLightBackground={!isMobile} />
            ))}
            {showAccessibilityLink && (
              <HelpfulLinkTag
                adminPanelSectionName={'accessibility' as HelpfulLinkAdminPanelSectionName}
                icon={<TrailWheelchair size="sm" />}
                iconBackground="light"
                id="accessibility"
                label={intl.formatMessage({ defaultMessage: 'Accessibility' })}
                onClick={id => onHelpfulLinkClick(id, 'accessibility')}
              />
            )}
          </div>
        )}
        <div className={styles.container}>
          <div className={classNames(styles.buttonContainer, styles.showMore)}>
            <div className={styles.action}>
              <Button
                onClick={onShowMoreClick}
                testId="show-more-tags-btn"
                text={<FormattedMessage defaultMessage="Show more" />}
                size="md"
                className={styles.showMoreButton}
                variant={isMobile ? 'default' : 'elevated'}
              />
            </div>
          </div>
        </div>
      </div>
      {isSignUpModalOpen && (
        <SignUpModal
          trigger="Plan Your Visit Show More"
          isOpen={isSignUpModalOpen}
          closeModal={() => setIsSignUpModalOpen(false)}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
          }}
          upsellTrigger="Plan Your Visit Show More"
        />
      )}
      {isShowMoreModalOpen && (
        <PlanYourVisitModal
          groups={sortedGroups}
          initialModal={initialModal}
          onCloseRequest={() => {
            setIsShowMoreModalOpen(false);
            setInitialModal(undefined);
          }}
          trailDetails={trailDetails}
          isAdminOrPLPUser={isAdminOrPLPUser}
        />
      )}
    </div>
  );
};

export default PlanYourVisit;
